import React, {ReactElement, useCallback, useEffect, useMemo, useState} from "react";
import {
	Alert, Avatar, Box,
	Button,
	Card, CardActions,
	CardHeader, CircularProgress,
	Container, Dialog, DialogContent, DialogTitle, Grid, LinearProgress, List, ListItem,
	ListItemAvatar,
	ListItemSecondaryAction, ListItemText,
	Stack, SxProps,
	Typography
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockBreakin from "@mui/icons-material/NewReleases";
import LockBlocked from "@mui/icons-material/Block";
import LockUnknown from "@mui/icons-material/HelpOutline";
import InfoIcon from "@mui/icons-material/Info";
import {green, grey, orange, red} from "@mui/material/colors";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {useNavigate} from "react-router-dom";
import {ConfirmDialog, ErrorAlert, Now, useFlag, ZonedDateTime} from "@variocube/app-ui";
import {BookingHeader} from "./booking-header";
import {useBooking} from "../../context/booking";
import {useLocalization} from "../../i18n";
import {notBlank} from "../../utils";
import {B2Occupancy} from "../../data/client.gen";
import {BookingBoxes, BookingEventType, useBookingClient, useBookingEvent} from "../../data/bookings";
import {BoxAvailability, useSiteApi} from "../../data/sites";
import {LocationVerification} from "../../component/location-verification";
import BookingOptions from "../../assets/booking_gw4j.svg";

enum View {
	BoxList = 'BoxList',
	OccupyingBoxes = 'OccupyingBoxes',
	LocationVerification = 'LocationVerification',
}

enum Action {
	OpenBox = 'OpenBox',
	OccupyBox = 'OccupyBox',
}

export function BookingBoxes() {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {booking, bookingFrom, bookingUntil, isBookingActive} = useBooking();
	const {verifyBookingLocation} = useSiteApi();

	const [view, setView] = useState(View.BoxList);
	const [action, setAction] = useState<Action>();
	const [cubeId, setCubeId] = useState<string>();
	const [availability, setAvailability] = useState<BoxAvailability>();

	const [occupancies, setOccupancies] = useState<B2Occupancy[]>(booking?.occupancies ?? []);
	const [occupancy, setOccupancy] = useState<B2Occupancy>();

	const {error: locationError, loading: locationLoading, execute: verifyLocation} = useAsyncCallback(verifyBookingLocation);

	useEffect(() => {
		if (booking !== undefined && booking.paymentStatus !== 'paid') {
			navigate(`/${booking.tenant.id}/bookings/${booking.id}`);
		}
	}, [booking]);

	if (booking !== undefined) {
		const client = useBookingClient(booking.id);
		useBookingEvent(client, BookingEventType.B2OccupancyEvent, event => {
			console.info('Lock status changed on box ', event.boxNumber)
			setOccupancies(prev => prev.map(o => {
				if (o.uuid === event.uuid) {
					o.lockStatus = event.lockStatus;
					o.openedTimes = event.openedTimes;
				}
				return o;
			}));
		});
	}

	function handleBoxListAction(action: Action, activeOccupancy: B2Occupancy|undefined) {
		setAction(action);
		setOccupancy(activeOccupancy);

		const autoCubeId = booking?.site.automaticallySelectCubeId;
		if (booking && notBlank(autoCubeId)) {
			verifyLocation(booking.id, autoCubeId as string)
				.then(availability => {
					setCubeId(autoCubeId);
					setAvailability(availability);
					if (action === 'OpenBox') setView(View.BoxList);
					else if (action === 'OccupyBox') setView(View.OccupyingBoxes);
				})
				.catch(console.error);
		}
		else {
			setCubeId(undefined);
			setAvailability(undefined);
			setView(View.LocationVerification);
		}
	}

	function handleLocationVerified(cubeId: string, availability: BoxAvailability) {
		setCubeId(cubeId);
		setAvailability(availability);
		switch (action) {
			case Action.OpenBox:
				setView(View.BoxList);
				break;
			case Action.OccupyBox:
				setView(View.OccupyingBoxes);
				break;
		}
	}

	function handleOccupyBoxesSuccess(occupancies: B2Occupancy[]) {
		setOccupancies(occupancies);
		setView(View.BoxList);
	}

	function resetView() {
		setView(View.BoxList);
		setAction(undefined);
		setOccupancy(undefined);
	}

	const notYetActive = useMemo(() => {
		return bookingFrom !== undefined && ZonedDateTime.compare(bookingFrom, Now.zonedDateTimeISO()) > 0
	}, [bookingFrom]);

	const isExpired = useMemo(() => {
		return bookingUntil !== undefined && ZonedDateTime.compare(bookingUntil, Now.zonedDateTimeISO()) < 1;
	}, [bookingUntil])

	return (
		<Container maxWidth="md">
			<Card sx={{ mt: 2 }}>
				<CardHeader
					title={<BookingHeader />}
				/>

				{isBookingActive && (
					<>
						{view === View.BoxList && (
							<BoxList verifiedCubeId={cubeId} onAction={handleBoxListAction} occupancies={occupancies} occupancy={occupancy}/>
						)}

						{view === View.LocationVerification && (
							<LocationVerifier onVerified={handleLocationVerified} onCancel={resetView} />
						)}

						{view === View.OccupyingBoxes && (
							<OccupyingBoxes verifiedCubeId={cubeId} availability={availability} onSuccess={handleOccupyBoxesSuccess} onCancel={resetView} />
						)}
					</>
				)}

				{!isBookingActive && (
					<>
						{booking?.state === 'Confirmed' && (
							<Stack>
								{notYetActive && <Alert severity="info">{t('booking.notYetActive')}</Alert>}
								{isExpired && <Alert severity="warning">{t('booking.expired')}</Alert>}
							</Stack>
						)}
						{booking?.state === 'Canceled' && (
							<Alert severity="warning">{t('booking.canceled')}</Alert>
						)}
					</>
				)}

				<Box my={2} />

				{view === View.BoxList && (
					<CardActions>
						<Button
							variant="contained"
							disableElevation
							onClick={() => navigate(`/${booking?.tenant.id}/bookings/${booking?.id}`)}
						>
							{t('actions.back')}
						</Button>
					</CardActions>
				)}

				{locationError && <ErrorAlert error={locationError} />}
				{locationLoading && <LinearProgress />}
			</Card>
		</Container>
	)
}

function OccupancyRow({occupancy, onClick}: { occupancy: B2Occupancy, onClick: () => void }) {
	const {lockStatus, boxNumber} = occupancy;
	const {t} = useLocalization();

	let sxProps: SxProps = {};
	let icon: ReactElement;
	switch (lockStatus ?? '') {
		case "Open":
			sxProps = { background: orange[400] };
			icon = <LockOpenIcon color="inherit" />;
			break;
		case "Closed":
			sxProps = { background: green[400] };
			icon = <LockIcon color="inherit" />;
			break;
		case "Breakin":
			sxProps = { background: red[400] };
			icon = <LockBreakin color="inherit" />;
			break;
		case "Blocked":
			sxProps = { background: grey[400] };
			icon = <LockBlocked color="inherit" />;
			break;
		default:
			icon = <LockUnknown color="inherit" />
			break;
	}

	return (
		<ListItem>
			<ListItemAvatar>
				<Avatar sx={{ ...sxProps, color: 'white' }}>
					{icon}
				</Avatar>
			</ListItemAvatar>
			<ListItemText
				primary={t('boxes.short').toUpperCase() + ' ' + boxNumber}
				secondary={lockStatus}
			/>
			<ListItemSecondaryAction>
				<Button
					variant="contained"
					disableElevation
					color="primary"
					size="large"
					onClick={onClick}
				>
					{t('boxes.open.button')}
				</Button>
			</ListItemSecondaryAction>
		</ListItem>
	)
}

interface BoxListProps {
	verifiedCubeId: string|undefined;
	onAction: (action: Action, occupancy?: B2Occupancy) => void;
	occupancies: B2Occupancy[];
	occupancy: B2Occupancy|undefined;
}

function BoxList({verifiedCubeId, onAction, occupancies, occupancy}: BoxListProps) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {booking, isBookingActive, release, open} = useBooking();

	const [releaseLocker, setReleaseLocker, clearReleaseLocker] = useFlag(false);
	const [activeOccupancy, setActiveOccupancy] = useState<B2Occupancy>();

	useEffect(() => {
		setActiveOccupancy(occupancy);
	}, [occupancy])

	/**
	 * Denotes whether the booking has occupied all of its boxes
	 */
	const isBookingFulfilled = useMemo(() => {
		if (booking !== undefined) {
			const totalBoxes = Object.values(booking.boxes as BookingBoxes).reduce((a, b) => a + b, 0);
			return totalBoxes === occupancies.length;
		}
		return false;
	}, [booking, occupancies]);

	/**
	 * Allows the user the release the booking, should the booking conditions and site support this behaviour
	 */
	const canRelease = useMemo(() => {
		if (booking !== undefined) {
			return occupancies.length > 0 && booking.site.enableRelease;
		}
		return false;
	}, [booking, occupancies]);

	/**
	 * Denotes whether the booking has been opened at least once
	 */
	const wasOpenedOnce = useMemo(() => {
		const openedOccupancies = occupancies
			.filter(o => (o.openedTimes ?? 0) > 0);
		return openedOccupancies.length > 0;
	}, [occupancies]);

	/**
	 * Allows the UI toggle release booking prompt during box open request
	 */
	const canAskReleaseLocker = useMemo(() =>
			wasOpenedOnce && booking?.site.askReleaseLocker
		, [booking, wasOpenedOnce])


	function handleOccupyRequest() {
		onAction(Action.OccupyBox);
	}

	function handleOpenBoxRequest(occupancy: B2Occupancy) {
		if (notBlank(verifiedCubeId) && occupancy.cubeId === verifiedCubeId) {
			setActiveOccupancy(occupancy);
		} else {
			onAction(Action.OpenBox, occupancy);
		}
	}

	const handleReleaseRequest = useCallback(async () => {
		if (booking !== undefined && canRelease) {
			await release();
			navigate(`/${booking.tenant.id}/bookings/${booking.id}`);
		}
	}, [booking, canRelease]);

	const handleOpenRequest = useCallback(async (releaseLockers: boolean) => {
		if (activeOccupancy !== undefined) {
			await open(activeOccupancy);
			if (releaseLockers) {
				await handleReleaseRequest();
			} else {
				setActiveOccupancy(undefined);
			}
		}
	}, [activeOccupancy]);


	const groupedOccupancies = useMemo(() => {
		let cubes: Record<string, string> = {};
		for (let cube of occupancies?.map(o => ({ id: o.cubeId, description: o.cubeDescription })) ?? []) {
			if (notBlank(cube.id) && cubes[cube.id as string] === undefined) {
				cubes[cube.id as string] = cube.description ?? '';
			}
		}
		return Object.entries(cubes)
			.map(([id, description]) => ({
				cube: description,
				occupancies: occupancies?.filter(o => o.cubeId === id) ?? []
			}));
	}, [occupancies]);
	return (
		<>
			{!isBookingFulfilled && (
				<>
					{occupancies.length === 0 && (
						<Stack
							direction="column"
							alignItems="center"
							gap={2}
							px={2}
						>
							<BookingOptions style={{ width: '200px', height: 'auto' }} />
							<Typography textAlign="center">
								{isBookingActive
									? t('booking.emptyOccupancies')
									: t('booking.emptyOccupanciesExpired')
								}
							</Typography>
							<Button
								variant="contained"
								color="primary"
								disabled={!isBookingActive}
								onClick={handleOccupyRequest}
							>
								{t('booking.occupy.button')}
							</Button>
						</Stack>
					)}
					{occupancies.length > 0 && (
						<Alert
							severity="warning"
							action={
								<Button
									variant="contained"
									color="primary"
									size="small"
									onClick={handleOccupyRequest}
								>
									{t('booking.occupy.button')}
								</Button>
							}
						>
							<Typography>{t('booking.occupyMoreHint')}</Typography>
						</Alert>
					)}
				</>
			)}
			{groupedOccupancies.map(({cube , occupancies }) => (
				<Stack
					key={cube}
					direction="column"
					gap={1}
				>
					<Typography px={2}><strong>{cube}</strong></Typography>
					<List>
						{occupancies.map(o => (
							<OccupancyRow
								key={o.uuid}
								occupancy={o}
								onClick={() => handleOpenBoxRequest(o)}
							/>
						))}
					</List>
				</Stack>
			))}
			{canRelease && (
				<Alert
					severity="warning"
					action={
						<Button
							variant="contained"
							color="secondary"
							size="small"
							disableElevation
							onClick={setReleaseLocker}
						>
							{t('booking.release.button')}
						</Button>
					}
				>
					<Typography>{t('booking.release.message')}</Typography>
				</Alert>
			)}

			<ConfirmDialog
				open={releaseLocker}
				onClose={clearReleaseLocker}
				title={t('booking.release.button')}
				cancel={t('actions.cancel')}
				onConfirm={handleReleaseRequest}
				color="warning"
			>
				<Typography gutterBottom>{t('booking.release.message')}</Typography>
				<Alert severity="warning">
					<Typography>{t('booking.release.warning')}</Typography>
				</Alert>
			</ConfirmDialog>

			<Dialog
				fullWidth
				maxWidth="xs"
				open={activeOccupancy !== undefined}
			>
				<DialogTitle>{t('boxes.open.confirmationTitle')}</DialogTitle>
				<DialogContent>
					<Typography gutterBottom>{t('boxes.open.confirmationMessage')}</Typography>
					<Stack
						direction="column"
						gap={1}
						mt={2}
					>
						<Button
							disableElevation
							variant="contained"
							color="primary"
							onClick={() => handleOpenRequest(false)}
						>
							{t('boxes.open.openAction')}
						</Button>
						{canRelease && canAskReleaseLocker && (
							<Button
								disableElevation
								variant="contained"
								color="primary"
								onClick={() => handleOpenRequest(true)}
							>
								{t('boxes.open.openAndReleaseAction')}
							</Button>
						)}
						<Button onClick={() => setActiveOccupancy(undefined)}>{t('actions.cancel')}</Button>
					</Stack>
				</DialogContent>
			</Dialog>
		</>
	)
}

interface OccupyingBoxesProps {
	verifiedCubeId: string|undefined;
	availability: BoxAvailability|undefined;
	onSuccess: (occupancies: B2Occupancy[]) => void;
	onCancel: () => void;
}

function OccupyingBoxes({verifiedCubeId, availability, onSuccess, onCancel}: OccupyingBoxesProps) {
	const {t} = useLocalization();
	const {booking, occupy} = useBooking();

	const {result: occupancies, error, loading} = useAsync(async () =>
		booking !== undefined && notBlank(verifiedCubeId)
			? await occupy(verifiedCubeId as string)
			: undefined
		, [booking, verifiedCubeId]);

	/**
	 * If the total occupied boxes are less than the total booked boxes.
	 * The cube does not have sufficient lockers.
	 */
	const insufficientLockers = useMemo(() => {
		if (booking !== undefined && occupancies !== undefined) {
			const totalBoxes = Object.values(booking.boxes as BookingBoxes).reduce((a, b) => a + b, 0);
			return occupancies.length < totalBoxes;
		}
		return false;
	}, [booking, occupancies]);

	useEffect(() => {
		if (occupancies !== undefined && !insufficientLockers) {
			onSuccess(occupancies);
		}
	}, [occupancies, insufficientLockers]);

	return (
		<Stack
			direction="column"
			alignItems="center"
			gap={2}
		>
			{loading && (
				<>
					<Typography>{t('booking.occupy.message')}</Typography>
					<CircularProgress />
				</>
			)}
			{error && (
				<>
					<InfoIcon fontSize="large" style={{ color: red[500] }} />
					<Typography textAlign="center">
						{t('booking.occupy.error')}
					</Typography>
					<Button variant="contained" disableElevation onClick={onCancel}>{t('actions.back')}</Button>
				</>
			)}
			{insufficientLockers && (
				<>
					<InfoIcon fontSize="large" style={{ color: orange[500] }} />
					<Typography textAlign="center">
						{t('booking.occupy.insufficientLockers')}
					</Typography>
					<Typography textAlign="center">
						{t('booking.occupy.persistingError')}
					</Typography>
					{notBlank(booking?.site.contactPhone) && (
						<Typography><strong>{t('sites.phoneNumber')}</strong>: <a href={'tel:' + booking?.site.contactPhone}>{booking?.site.contactPhone}</a></Typography>
					)}
					<Grid container direction="column" alignContent="center">
						<Grid item xs={12}>
							<Typography><strong>{t('boxes.availability')}</strong></Typography>
						</Grid>
						{Object.entries(availability ?? {}).map(([boxType, quantity]) => (
							<Grid key={'boxType' + boxType} item sm={4} xs={8} style={{ display: 'flex' }}>
								<strong style={{ flexGrow: 1 }}>{boxType}</strong>
								<span>{quantity}</span>
							</Grid>
						))}
					</Grid>
					<Button variant="contained" disableElevation onClick={onCancel}>{t('actions.back')}</Button>
				</>
			)}
		</Stack>
	)
}

interface LocationVerifierProps {
	onVerified: (cubeId: string, availability: BoxAvailability) => void;
	onCancel: () => void;
	occupancy?: B2Occupancy;
}

function LocationVerifier({onVerified, onCancel, occupancy}: LocationVerifierProps) {
	const {booking} = useBooking();
	return (
		<LocationVerification
			bookingId={booking?.id ?? ''}
			onCancel={onCancel}
			onVerified={onVerified}
			qrcodeReaderEnabled={booking?.site.enableCubeQrCode}
			requiredCubeId={occupancy?.cubeId}
			cubeDescription={occupancy?.cubeDescription}
			automaticallySelectCubeId={booking?.site.automaticallySelectCubeId}
		/>
	)
}
